import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "components-lib";
import { FaFilePdf } from "react-icons/fa6";
import UploadedFileModal from "./UploadedFileModal";
import { axiosInstance } from "config/api";

/**
 * PastAppoinmentModal - Patient Appointment History Viewer
 *
 * This modal component provides a comprehensive interface for viewing and managing
 * a patient's historical appointment data. It displays past appointments in a tabular
 * format with prescription viewing capabilities and advanced PDF handling.
 *
 * Key Features:
 * - Displays past appointments with date, doctor, and status information
 * - Prescription viewing with embedded PDF preview capabilities
 * - Cross-origin PDF handling with multiple fallback methods
 * - Status-based color coding for appointment states
 * - Responsive table layout with proper data formatting
 * - Advanced PDF caching and proxy support for blocked content
 *
 * Component Architecture:
 * - Main modal with appointment history table
 * - Integrated prescription preview modal with iframe support
 * - PDF viewer with fallback options for blocked content
 * - File modal integration for document viewing
 * - Loading states and error handling throughout
 *
 * Props:
 * @param {string|number} id - Patient ID for fetching appointment history
 * @param {boolean} isOpen - Modal visibility state
 * @param {Function} onClose - Callback to close the modal
 * @param {string} userName - Patient name for display purposes
 *
 * State Management:
 * - pastAppointments: Array of historical appointment data
 * - selectedPrescription: Currently selected prescription for preview
 * - uploadedFile: File modal state for document viewing
 * - PDF handling states: loading, error, caching, and preview modes
 * - URL management for prescription document access
 *
 * PDF Handling Features:
 * - Multiple URL format support (Google Drive, Dropbox, direct links)
 * - Cross-origin proxy support for blocked content
 * - Local PDF caching for improved performance
 * - Fallback download and alternative viewer options
 * - Memory leak prevention with URL cleanup
 *
 * API Integration:
 * - GET /api/appointment/past/patient - Fetch patient appointment history
 * - PDF proxy support through backend when direct access fails
 * - Error handling with graceful degradation
 *
 * Data Display:
 * - Formatted date and time display with localization
 * - Doctor name and appointment status with color coding
 * - Prescription availability indicators
 * - Status badges with appropriate color schemes
 *
 * Usage:
 * ```jsx
 * <PastAppoinmentModal
 *   id={patientId}
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   userName={patient.name}
 * />
 * ```
 *
 * Related Components:
 * - UploadedFileModal: For viewing uploaded documents
 * - ConsultationToolbar: Parent component that triggers this modal
 *
 * Browser Compatibility:
 * - Handles iframe restrictions in modern browsers
 * - Provides fallback options for blocked content
 * - Cross-origin handling with multiple strategies
 *
 * @component
 * @since 1.0.0
 * @author Kifayti Health
 */
const PastAppoinmentModal = ({ id, isOpen, code, onClose, userName }) => {
  const [pastAppointments, setPastAppointments] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [isPrescriptionViewOpen, setIsPrescriptionViewOpen] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [cachedPdfUrl, setCachedPdfUrl] = useState(null);
  const [fetchingPdf, setFetchingPdf] = useState(false);

  const openFileModal = (id, imageUrl, comment) => {
    setUploadedFile({ id, imageUrl, comment });
  };

  const closeFileModal = () => {
    setUploadedFile(null);
  };

  const openPrescriptionPreview = (prescriptionUrl, appointmentData) => {
    // Prevent any potential page navigation
    if (!prescriptionUrl) {
      console.error("No prescription URL provided");
      return;
    }

    setPdfLoadError(false);
    setIsLoadingPdf(true);

    // Enhanced URL formatting for different hosting services
    let formattedUrl = prescriptionUrl;

    // Handle Google Drive links
    if (prescriptionUrl.includes("drive.google.com")) {
      if (prescriptionUrl.includes("/file/d/")) {
        // Extract file ID from Google Drive URL
        const fileIdMatch = prescriptionUrl.match(
          /\/file\/d\/([a-zA-Z0-9-_]+)/
        );
        if (fileIdMatch) {
          const fileId = fileIdMatch[1];
          formattedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
        }
      } else {
        formattedUrl = prescriptionUrl.replace("/view", "/preview");
      }
    }
    // Handle other services that might block iframe
    else if (prescriptionUrl.includes("dropbox.com")) {
      formattedUrl = prescriptionUrl
        .replace("?dl=0", "?dl=1")
        .replace("?dl=1", "?raw=1");
    }

    console.log("Original URL:", prescriptionUrl);
    console.log("Formatted URL:", formattedUrl);

    setSelectedPrescription({
      url: formattedUrl,
      originalUrl: prescriptionUrl,
      appointment: appointmentData,
    });
    setIsPrescriptionViewOpen(true);
  };

  // Function to fetch PDF and cache it locally
  const fetchAndCachePdf = async (url) => {
    setFetchingPdf(true);
    try {
      console.log("Fetching PDF from URL:", url);

      // Try multiple methods to fetch the PDF
      let response;

      // Method 1: Direct fetch (works for same-origin or CORS-enabled URLs)
      try {
        response = await fetch(url, {
          method: "GET",
          mode: "cors",
          headers: {
            Accept: "application/pdf,*/*",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (corsError) {
        console.log("Direct fetch failed, trying proxy method:", corsError);

        // Method 2: Use your backend as proxy
        try {
          response = await axiosInstance.get("/api/proxy/pdf", {
            params: { url },
            responseType: "blob",
          });

          // Convert axios response to fetch-like response
          response = {
            ok: true,
            blob: () => Promise.resolve(response.data),
          };
        } catch (proxyError) {
          console.log(
            "Proxy method failed, using alternative approach:",
            proxyError
          );

          // Method 3: For Google Drive, try direct download link
          if (url.includes("drive.google.com")) {
            const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
            if (fileIdMatch) {
              const fileId = fileIdMatch[1];
              const downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;

              response = await fetch(downloadUrl, {
                method: "GET",
                mode: "no-cors",
              });
            }
          }
        }
      }

      if (response && response.ok) {
        const blob = await response.blob();

        // Verify it's a PDF
        if (blob.type.includes("pdf") || blob.type === "application/pdf") {
          const objectUrl = URL.createObjectURL(blob);
          setCachedPdfUrl(objectUrl);
          console.log("PDF cached successfully with object URL:", objectUrl);
          return objectUrl;
        } else {
          console.warn("Fetched content is not a PDF:", blob.type);
          throw new Error("Invalid PDF content");
        }
      } else {
        throw new Error("Failed to fetch PDF");
      }
    } catch (error) {
      console.error("Error fetching and caching PDF:", error);
      return null;
    } finally {
      setFetchingPdf(false);
    }
  };

  const closePrescriptionPreview = () => {
    // Clean up cached URL to prevent memory leaks
    if (cachedPdfUrl) {
      URL.revokeObjectURL(cachedPdfUrl);
      setCachedPdfUrl(null);
    }

    setSelectedPrescription(null);
    setIsPrescriptionViewOpen(false);
    setPdfLoadError(false);
    setIsLoadingPdf(false);
    setFetchingPdf(false);
  };

  const fetchPastAppointments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log("Fetching past appointments for patient ID:", id);
      const response = await axiosInstance.get(
        `api/appointment/past/patient?patient_id=${id}`
      );
      console.log("Past appointments API response:", response);
      console.log("Past appointments data:", response.data);

      if (response.data && response.data.pastAppointments) {
        console.log(
          "Setting past appointments:",
          response.data.pastAppointments
        );
        setPastAppointments(response.data.pastAppointments);
      } else {
        console.log("No past appointments found in response");
        setPastAppointments([]);
      }
    } catch (error) {
      console.error("Error fetching past appointments:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError("Failed to fetch past appointments. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && id) {
      fetchPastAppointments();
    }
  }, [isOpen, id]);

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "green";
      case "CANCELLED":
        return "red";
      case "BOOKED":
        return "blue";
      case "ARRIVED":
        return "orange";
      default:
        return "gray";
    }
  };

  function formatDateTime(date, time) {
    if (!date) return "N/A";

    let formattedDate = date;
    if (date.includes("-")) {
      const [year, month, day] = date.split("-");
      formattedDate = `${day}-${month}-${year.slice(-2)}`;
    }

    if (!time) return formattedDate;

    const [hour, minute] = time.split(":");
    let h = parseInt(hour, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${formattedDate} ${h}:${minute} ${ampm}`;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent minW="600px">
        <ModalHeader>Past Appointments - {userName} </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <div className="container mx-auto p-4" style={{ minWidth: "550px" }}>
            <div className="bg-gray-100 py-4 px-4 md:px-10">
              <div
                className="manage-roles-container p-4 bg-white shadow-md border-t-4 border-primary"
                style={{ minWidth: "500px" }}
              >
                <div className="mt-4 mb-4 flex items-center justify-between">
                  <h1 className="text-xl font-bold">Patient Code: {code}</h1>
                </div>
                <div className="flex justify-between items-center border-b pb-2 mb-4">
                  <h2 className="text-2xl font-bold">Past Appointments</h2>
                </div>

                {isLoading ? (
                  <Text textAlign="center" py={4}>
                    Loading appointments...
                  </Text>
                ) : error ? (
                  <Text textAlign="center" color="red.500" py={4}>
                    {error}
                  </Text>
                ) : (
                  <div className="overflow-x-auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th width="200px" minWidth="200px">
                            Date & Time
                          </Th>
                          <Th>Doctor</Th>
                          <Th>Status</Th>
                          <Th>Prescription</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {pastAppointments.length > 0 ? (
                          pastAppointments.map((appointment, index) => (
                            <Tr key={index}>
                              <Td minWidth="200px">
                                {formatDateTime(
                                  appointment.appointment_date,
                                  appointment.appointment_time
                                )}
                              </Td>
                              <Td>{appointment.doctor_name || "N/A"}</Td>
                              <Td>
                                <Badge
                                  colorScheme={getStatusColor(
                                    appointment.status
                                  )}
                                >
                                  {appointment.status || "N/A"}
                                </Badge>
                              </Td>
                              <Td>
                                {appointment.prescription ||
                                appointment.prescription_file ? (
                                  <Button
                                    size="sm"
                                    colorScheme="blue"
                                    leftIcon={<FaFilePdf />}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const prescriptionUrl =
                                        appointment.prescription ||
                                        appointment.prescription_file;
                                      // Open prescription preview within modal
                                      openPrescriptionPreview(
                                        prescriptionUrl,
                                        appointment
                                      );
                                    }}
                                  >
                                    View
                                  </Button>
                                ) : (
                                  <Text color="gray.500">No prescription</Text>
                                )}
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={4} textAlign="center">
                              No past appointments found
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
      {uploadedFile && (
        <UploadedFileModal
          closeModal={closeFileModal}
          user_id={id}
          file_id={uploadedFile.id}
          file={uploadedFile}
        />
      )}

      {/* Prescription Preview Modal */}
      {selectedPrescription && (
        <Modal
          isOpen={isPrescriptionViewOpen}
          onClose={closePrescriptionPreview}
          size="6xl"
        >
          <ModalOverlay />
          <ModalContent maxW="90vw" maxH="95vh">
            <ModalHeader>
              <Flex align="center">
                <Box>
                  <Text fontSize="lg" fontWeight="bold">
                    Prescription Preview
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {formatDateTime(
                      selectedPrescription.appointment.appointment_date,
                      selectedPrescription.appointment.appointment_time
                    )}{" "}
                    - Dr. {selectedPrescription.appointment.doctor_name}
                  </Text>
                </Box>
                <Spacer />
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() =>
                    window.open(selectedPrescription.url, "_blank")
                  }
                  mr={3}
                >
                  Open in New Tab
                </Button>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={0}>
              <Box
                width="100%"
                height="80vh"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                overflow="hidden"
                position="relative"
                bg="gray.50"
              >
                {/* Primary PDF Viewer - Try multiple approaches */}
                {!pdfLoadError ? (
                  <>
                    {/* Method 1: Try iframe first */}
                    <iframe
                      src={cachedPdfUrl || selectedPrescription.url}
                      width="100%"
                      height="100%"
                      style={{
                        border: "none",
                        display: pdfLoadError ? "none" : "block",
                      }}
                      title="Prescription Preview"
                      onError={(e) => {
                        console.error(
                          "Iframe failed, trying PDF caching...",
                          e
                        );
                        if (!cachedPdfUrl) {
                          // Try to fetch and cache the PDF
                          fetchAndCachePdf(
                            selectedPrescription.originalUrl ||
                              selectedPrescription.url
                          ).then((cachedUrl) => {
                            if (cachedUrl) {
                              console.log(
                                "PDF cached, retrying with cached URL"
                              );
                              // The iframe will automatically reload with the new cachedPdfUrl
                            } else {
                              console.log(
                                "PDF caching failed, showing fallback UI"
                              );
                              setPdfLoadError(true);
                              setIsLoadingPdf(false);
                            }
                          });
                        } else {
                          setPdfLoadError(true);
                          setIsLoadingPdf(false);
                        }
                      }}
                      onLoad={(e) => {
                        console.log("Prescription iframe loaded successfully");
                        setIsLoadingPdf(false);
                        // Check if iframe actually loaded content or was blocked
                        setTimeout(() => {
                          try {
                            const iframe = e.target;
                            if (iframe.contentDocument === null) {
                              console.log(
                                "Iframe blocked by browser, trying PDF caching..."
                              );
                              if (!cachedPdfUrl) {
                                // Try to fetch and cache the PDF
                                fetchAndCachePdf(
                                  selectedPrescription.originalUrl ||
                                    selectedPrescription.url
                                ).then((cachedUrl) => {
                                  if (cachedUrl) {
                                    console.log(
                                      "PDF cached successfully, iframe should reload"
                                    );
                                    // Force a re-render to use the cached URL
                                    setIsLoadingPdf(false);
                                  } else {
                                    console.log(
                                      "PDF caching failed, showing fallback UI"
                                    );
                                    setPdfLoadError(true);
                                  }
                                });
                              } else {
                                setPdfLoadError(true);
                              }
                            }
                          } catch (error) {
                            console.log(
                              "Cross-origin restriction detected, trying PDF caching..."
                            );
                            if (!cachedPdfUrl) {
                              fetchAndCachePdf(
                                selectedPrescription.originalUrl ||
                                  selectedPrescription.url
                              ).then((cachedUrl) => {
                                if (!cachedUrl) {
                                  setPdfLoadError(true);
                                }
                              });
                            } else {
                              setPdfLoadError(true);
                            }
                          }
                        }, 1000);
                      }}
                    />
                  </>
                ) : (
                  /* Fallback UI when iframe fails */
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    height="100%"
                    p={8}
                    textAlign="center"
                  >
                    <Box mb={6}>
                      <FaFilePdf size={64} color="#CBD5E0" />
                    </Box>
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="gray.700"
                      mb={2}
                    >
                      PDF Preview Blocked
                    </Text>
                    <Text color="gray.600" mb={6} maxW="400px">
                      Your browser has blocked the PDF preview for security
                      reasons. You can still view the prescription using the
                      options below.
                    </Text>

                    <Flex direction={{ base: "column", md: "row" }} gap={4}>
                      <Button
                        colorScheme="blue"
                        size="lg"
                        leftIcon={<FaFilePdf />}
                        onClick={() =>
                          window.open(selectedPrescription.url, "_blank")
                        }
                      >
                        Open in New Tab
                      </Button>

                      <Button
                        colorScheme="green"
                        size="lg"
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = selectedPrescription.url;
                          link.download = `prescription-${formatDateTime(
                            selectedPrescription.appointment.appointment_date,
                            selectedPrescription.appointment.appointment_time
                          ).replace(/[^a-zA-Z0-9]/g, "-")}.pdf`;
                          link.target = "_blank";
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        Download PDF
                      </Button>

                      <Button
                        colorScheme="purple"
                        size="lg"
                        onClick={() => {
                          // Try Google Docs Viewer as alternative
                          const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
                            selectedPrescription.originalUrl ||
                              selectedPrescription.url
                          )}&embedded=true`;
                          window.open(googleViewerUrl, "_blank");
                        }}
                      >
                        Try Alternative Viewer
                      </Button>
                    </Flex>

                    <Text fontSize="sm" color="gray.500" mt={6}>
                      Having trouble? Contact support for assistance.
                    </Text>
                  </Flex>
                )}

                {/* Loading overlay */}
                {(isLoadingPdf || fetchingPdf) && (
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="whiteAlpha.900"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                  >
                    <Text mb={4} color="gray.600">
                      {fetchingPdf
                        ? "Downloading and caching PDF..."
                        : "Loading prescription..."}
                    </Text>
                    {fetchingPdf && (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        mb={4}
                        textAlign="center"
                      >
                        Chrome blocked the preview, so we're downloading it for
                        you
                      </Text>
                    )}
                    <Box
                      w={8}
                      h={8}
                      border="2px solid #E2E8F0"
                      borderTop="2px solid #3182CE"
                      borderRadius="50%"
                      animation="spin 1s linear infinite"
                    />
                    <style jsx>{`
                      @keyframes spin {
                        0% {
                          transform: rotate(0deg);
                        }
                        100% {
                          transform: rotate(360deg);
                        }
                      }
                    `}</style>
                  </Box>
                )}
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      <ModalCloseButton />
    </Modal>
  );
};

export default PastAppoinmentModal;
